/**
 * ViewImpactDialog - confirm a settings save the server refused because it
 * would stop producing a table a published view reads (#2000, #2155).
 *
 * The PATCH answers 409 with one `details` entry per affected view
 * (`field` = view name, `message` = what breaks, `code` = `view_binding_*`).
 * A deliberate narrowing goes through only when the operator confirms here;
 * the form then resends the same patch with `acknowledge_view_impact=true`.
 * Nothing is remembered: the next refused save asks again.
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export interface ViewImpact {
  view: string;
  detail: string;
}

/**
 * The affected views when `err` is the view-binding 409, else null.
 *
 * Keyed on the `view_binding_` detail code, not on status alone: the
 * credentials ETag 409 (`ConflictError`) and any other 409 keep their own
 * handling.
 */
export function viewBindingImpacts(err: unknown): ViewImpact[] | null {
  if (!err || typeof err !== 'object') return null;
  const { status, details } = err as { status?: unknown; details?: unknown };
  if (status !== 409 || !Array.isArray(details)) return null;
  const impacts = details.filter(
    (d): d is { field?: unknown; message?: unknown; code: string } =>
      !!d && typeof d === 'object' &&
      typeof (d as { code?: unknown }).code === 'string' &&
      (d as { code: string }).code.startsWith('view_binding_'),
  );
  if (impacts.length === 0) return null;
  return impacts.map((d) => ({
    view: typeof d.field === 'string' ? d.field : '(unnamed view)',
    detail: typeof d.message === 'string' ? d.message : '',
  }));
}

export interface ViewImpactDialogProps {
  /** The refused save's affected views; the dialog is open while non-null. */
  impacts: ViewImpact[] | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ViewImpactDialog: React.FC<ViewImpactDialogProps> = ({
  impacts,
  onConfirm,
  onCancel,
}) => {
  const count = impacts?.length ?? 0;
  return (
    <AlertDialog open={impacts !== null} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {count === 1 ? 'This save breaks a published view' : `This save breaks ${count} published views`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {count === 1 ? 'This view' : 'These views'} will stop refreshing. Nothing has been saved yet.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-2 text-sm" aria-label="Affected views">
          {(impacts ?? []).map((i) => (
            <li key={i.view}>
              <code className="font-mono font-medium">{i.view}</code>
              {i.detail && <p className="text-muted-foreground">{i.detail}</p>}
            </li>
          ))}
        </ul>
        <p className="text-sm">Save anyway?</p>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={onConfirm}>
            Save anyway
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
